const CACHE_KEY = "dusk_product_badges_cache";
const CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Clean a tag by removing extra spaces and special characters
 * @param {string} tag - Tag to clean
 * @returns {string} - Cleaned tag
 */
const cleanTag = (tag) => {
  return tag.trim().toLowerCase().normalize("NFKC");
};

/**
 * Get SVG icon based on icon type
 * @param {string} iconType - Icon type (Tag, Box, None, or null)
 * @returns {string} - SVG HTML string or empty string
 */
const getBadgeIcon = (iconType) => {
  if (!iconType || iconType === "None" || iconType === null) {
    return "";
  }

  switch (iconType.toLowerCase()) {
    case "tag":
      return `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon--tag" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M13.2538 7.88857L12.9046 3.57025C12.8728 3.31624 12.6823 3.12575 12.4283 3.09396L8.10996 2.74469C7.9512 2.74469 7.79239 2.8082 7.68127 2.91932L0.933897 9.66669C0.72748 9.87311 0.711617 10.2065 0.918034 10.4129L5.58563 15.0805C5.79204 15.2869 6.12543 15.2711 6.33184 15.0646L13.0792 8.31727C13.1903 8.20616 13.2538 8.04733 13.2538 7.88857ZM10.3644 5.63415C10.0627 5.33249 10.0627 4.82448 10.3802 4.50696C10.6978 4.18943 11.2058 4.18943 11.5074 4.49109C11.8091 4.79275 11.8091 5.30077 11.4916 5.61829C11.1899 5.91995 10.666 5.93581 10.3644 5.63415Z" fill="currentColor"/>
      </svg>`;
    case "box":
      return `<svg width="16" height="16" viewBox="0 0 10 11" fill="none" class="icon icon--box" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 4.31615L3 1.98972M1.12 3.22242L5 5.48609L8.88 3.22242M5 10V5.48161M9 7.27462V3.6886C8.99984 3.53138 8.95869 3.37698 8.88068 3.24087C8.80268 3.10476 8.69055 2.99173 8.55556 2.91312L5.44444 1.12011C5.30932 1.04142 5.15603 1 5 1C4.84397 1 4.69068 1.04142 4.55556 1.12011L1.44444 2.91312C1.30945 2.99173 1.19732 3.10476 1.11932 3.24087C1.04131 3.37698 1.00016 3.53138 1 3.6886V7.27462C1.00016 7.43184 1.04131 7.58624 1.11932 7.72235C1.19732 7.85846 1.30945 7.97149 1.44444 8.0501L4.55556 9.84311C4.69068 9.9218 4.84397 9.96322 5 9.96322C5.15603 9.96322 5.30932 9.9218 5.44444 9.84311L8.55556 8.0501C8.69055 7.97149 8.80268 7.85846 8.88068 7.72235C8.95869 7.58624 8.99984 7.43184 9 7.27462Z" stroke="#474546" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `;
    default:
      return "";
  }
};

/**
 * Main function to populate product badges across all badge elements on the page
 * Includes self-healing functionality for carousel integration
 */
const populateProductBadges = () => {
  // Fix missing product IDs from wishlist buttons
  // This ensures carousel-loaded products get proper badge data
  const allProductCards = document.querySelectorAll(".product-card-wrapper");

  allProductCards.forEach((cardWrapper) => {
    const badgeElement = cardWrapper.querySelector("product-badge");
    const wishlistButton = cardWrapper.querySelector(
      ".swym-add-to-wishlist-view-product"
    );

    if (badgeElement && wishlistButton && wishlistButton.dataset.productId) {
      try {
        const correctId = JSON.parse(wishlistButton.dataset.productId);
        if (correctId) {
          badgeElement.dataset.productId = String(correctId);
        }
      } catch (e) {
        // Silently handle JSON parsing errors
      }
    }
  });

  // Find all badge elements that need populating
  const allBadgeElements = document.querySelectorAll("product-badge");
  if (allBadgeElements.length === 0) {
    return;
  }

  // Fetch badge data and render badges
  fetchProductBadges(allBadgeElements).then((data) => {
    if (!data || !data.badges) return;

    allBadgeElements.forEach((badge) => {
      const productId = badge.dataset.productId;
      const badgeData = data.badges[productId];

      if (badgeData && typeof badgeData === "object") {
        const badgeIcon = getBadgeIcon(badgeData.icon);
        const badgeStyle = badgeData.style?.toLowerCase() || "sale";
        const badgeType = badgeData.type?.toLowerCase() || "default";

        badge.innerHTML = `
          <div class="card__badge card__badge--${badgeType} card__badge--${badgeStyle}">
            <span class="badge">
              ${badgeIcon}${badgeData.label}
            </span>
          </div>
        `;
      } else {
        badge.innerHTML = "";
      }
    });
  });
};

/**
 * Check if a badge is currently valid based on start/end dates
 * @param {Object} badge - Badge object with optional start_date_time and end_date_time
 * @returns {boolean} - True if badge is valid for current time
 */
const isBadgeValid = (badge) => {
  if (!badge) return false;

  const now = Date.now();
  const isBeforeStart = badge.start_date_time && badge.start_date_time > now;
  const isAfterEnd = badge.end_date_time && badge.end_date_time < now;

  return !(isBeforeStart || isAfterEnd);
};

/**
 * Parse Shopify metaobject fields into a structured badge object
 * @param {Array} fields - Array of field objects with key/value pairs
 * @param {string} id - Metaobject ID
 * @returns {Object} - Parsed badge data object
 */
const parseBadgeFields = (fields, id) => {
  const badgeData = {};

  fields.forEach(({ key, value }) => {
    if (key === "start_date_time") {
      badgeData.start_date_time = value ? Date.parse(value) : undefined;
    } else if (key === "end_date_time") {
      badgeData.end_date_time = value ? Date.parse(value) : undefined;
    } else if (key === "criteria_collections") {
      if (value) {
        try {
          badgeData.criteria_collections = JSON.parse(value);
        } catch {
          badgeData.criteria_collections = value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        }
      } else {
        badgeData.criteria_collections = [];
      }
    } else if (key === "criteria_tags") {
      if (value) {
        // First try to parse as JSON array
        if (value.startsWith("[") && value.endsWith("]")) {
          try {
            const parsed = JSON.parse(value);
            badgeData.criteria_tags = Array.isArray(parsed) 
              ? parsed.map(tag => cleanTag(tag))
              : [cleanTag(value)];
          } catch {
            // Fallback to comma-separated parsing
            badgeData.criteria_tags = value
              .split(",")
              .map((tag) => cleanTag(tag))
              .filter(Boolean);
          }
        } else {
          // Handle comma-separated values
          badgeData.criteria_tags = value
            .split(",")
            .map((tag) => cleanTag(tag))
            .filter(Boolean);
        }
      } else {
        badgeData.criteria_tags = [];
      }
    } else if (key === "criteria_tags_exclude") {
      badgeData.criteria_tags_exclude = value
        ? value
            .split(",")
            .map((tag) => cleanTag(tag))
            .filter(Boolean)
        : [];
    } else if (
      typeof value === "string" &&
      value.startsWith("[") &&
      value.endsWith("]")
    ) {
      try {
        badgeData[key] = JSON.parse(value);
      } catch {
        badgeData[key] = value;
      }
    } else if (key === "priority") {
      badgeData.priority = Number(value);
    } else {
      badgeData[key] = value;
    }
  });

  badgeData.id = id;
  return badgeData;
};

/**
 * Extract short product ID from Shopify GID format
 * @param {string} gid - Shopify Global ID (e.g., "gid://shopify/Product/12345")
 * @returns {string} - Short product ID (e.g., "12345")
 */
const getShortProductId = (gid) =>
  String(gid).replace("gid://shopify/Product/", "");

/**
 * Determine stock status based on product availability
 * @param {Object} product - Product object with availableForSale and variant data
 * @returns {string} - "In stock", "Pre-order", or "Out of stock"
 */
const getStockStatus = (product) => {
  if (!product.availableForSale) {
    return "Out of stock";
  } else {
    // When availableForSale is false, check currentlyNotInStock
    const currentlyNotInStock =
      product.selectedOrFirstAvailableVariant?.currentlyNotInStock;
    return currentlyNotInStock ? "Pre-order" : "In stock";
  }
};

/**
 * Check if a product belongs to any of the specified collections
 * @param {Array} productCollections - Array of collection objects from product data
 * @param {Array} requiredCollections - Array of collection handles/IDs to check against
 * @returns {boolean} - True if product belongs to at least one of the required collections
 */
const isProductInCollections = (productCollections, requiredCollections) => {
  if (!requiredCollections || requiredCollections.length === 0) {
    return true;
  }

  if (!productCollections || productCollections.length === 0) {
    return false;
  }

  return productCollections.some((collection) =>
    requiredCollections.some(
      (required) =>
        collection.handle.toLowerCase() === required.toLowerCase() ||
        collection.id.replace(/gid:\/\/shopify\/Collection\//, "") === required
    )
  );
};

/**
 * Fetch product badge data from Shopify GraphQL API with caching
 * @param {NodeList} productBadgeElements - DOM elements containing product badge placeholders
 * @returns {Promise<Object>} - Object containing badges keyed by product ID
 */
const fetchProductBadges = async (productBadgeElements) => {
  // Extract unique product IDs from badge elements
  const productIds = [
    ...new Set(
      Array.from(
        productBadgeElements,
        (badge) => badge.dataset.productId
      ).filter(Boolean)
    ),
  ];

  if (productIds.length === 0) {
    return { badges: {} };
  }

  // Load existing cache
  let cache = {};
  let cacheTimestamp = 0;
  try {
    const cacheRaw = sessionStorage.getItem(CACHE_KEY);
    if (cacheRaw) {
      const parsed = JSON.parse(cacheRaw);
      cache = parsed.data || {};
      cacheTimestamp = parsed.timestamp || 0;
    }
  } catch {
    cache = {};
    cacheTimestamp = 0;
  }

  // Check if cache is still valid
  const cacheValid =
    cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION_MS;
  const badgesToReturn = {};
  let missingProductIds = [];

  if (cacheValid) {
    productIds.forEach((pid) => {
      if (cache.hasOwnProperty(pid) && isBadgeValid(cache[pid])) {
        badgesToReturn[pid] = cache[pid];
      } else if (cache.hasOwnProperty(pid) && cache[pid] === null) {
        badgesToReturn[pid] = null;
      } else {
        missingProductIds.push(pid);
      }
    });
  } else {
    // Cache invalid - fetch all products
    missingProductIds = [...productIds];
  }

  if (missingProductIds.length === 0) {
    return { badges: badgesToReturn };
  }

  // Helper function to split arrays into smaller chunks
  const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  // Helper function for GraphQL requests
  const fetchGraphQL = async (query) => {
    const response = await fetch(
      `https://${window.Shopify.shop}/api/2025-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token":
            "fa461acd789eda3169914d681fe74ba9",
        },
        body: JSON.stringify({ query }),
      }
    );
    return await response.json();
  };

  // Prepare GraphQL queries
  const metaobjectsQuery = `
    query { 
      metaobjects(first: 50, type: "product_card_badge", sortKey: "id") { 
        edges { 
          node { 
            id 
            fields { 
              key 
              value 
            } 
          } 
        } 
      } 
    }
  `;

  // Batch product IDs for efficient API calls
  const productIdBatches = chunkArray(missingProductIds, 200);
  const productBatchPromises = productIdBatches.map((ids) => {
    const queryString = `id:${ids.join(" OR id:")}`;
    return fetchGraphQL(`
      query { 
        products(first: 250, query: "${queryString}") { 
          edges { 
            node { 
              id 
              handle 
              tags 
              availableForSale 
              selectedOrFirstAvailableVariant { 
                availableForSale
                currentlyNotInStock
                compareAtPrice { 
                  amount 
                } 
                price { 
                  amount 
                } 
              }
              collections(first: 40) {
                edges {
                  node {
                    id
                    handle
                  }
                }
              }
            } 
          } 
        } 
      }
    `);
  });

  // Execute all GraphQL queries in parallel
  const [metaobjectsResponse, ...productResponses] = await Promise.all([
    fetchGraphQL(metaobjectsQuery),
    ...productBatchPromises,
  ]);

  // Extract data from responses
  const metaobjectsEdges = metaobjectsResponse?.data?.metaobjects?.edges || [];
  const productsEdges = productResponses.flatMap(
    (res) => res?.data?.products?.edges || []
  );

  if (!metaobjectsEdges.length) {
    return { badges: badgesToReturn };
  }

  // Process badge definitions and product data
  const availableBadges = metaobjectsEdges
    .map(({ node }) => parseBadgeFields(node.fields, node.id))
    .filter(isBadgeValid);

  const productData = productsEdges.map(({ node }) => ({
    id: node.id,
    handle: node.handle,
    tags: node.tags ? node.tags.map((tag) => cleanTag(tag)) : [],
    availableForSale: node.availableForSale,
    selectedOrFirstAvailableVariant: node.selectedOrFirstAvailableVariant,
    compareAtPrice:
      Number(node.selectedOrFirstAvailableVariant?.compareAtPrice?.amount) ||
      undefined,
    price: Number(node.selectedOrFirstAvailableVariant?.price.amount),
    collections: node.collections.edges.map((edge) => edge.node),
  }));

  // Process each missing product ID to find applicable badges
  missingProductIds.forEach((shortIdToProcess) => {
    const productGid = `gid://shopify/Product/${shortIdToProcess}`;
    const product = productData.find((p) => p.id === productGid);

    if (product) {
      // Get the actual stock status using the stock logic
      const stockStatus = getStockStatus(product);

      // Check for exclusion tags - if found, no badges will be shown
      let shouldExcludeAllBadges = false;

      // Check each badge against product criteria
      availableBadges.forEach((badge) => {
        if (
          badge.criteria_tags_exclude &&
          badge.criteria_tags_exclude.length > 0
        ) {
          if (
            product.tags.some((productTag) =>
              badge.criteria_tags_exclude.includes(productTag)
            )
          ) {
            shouldExcludeAllBadges = true;
          }
        }
      });

      // If any badge has exclusion criteria that matches, skip all badges for this product
      if (shouldExcludeAllBadges) {
        badgesToReturn[shortIdToProcess] = null;
        cache[shortIdToProcess] = null;
        return;
      }

      const applicableBadges = [];

      availableBadges.forEach((badge) => {
        let badgeApplies = true;

        // Check collection criteria
        if (
          badge.criteria_collections &&
          badge.criteria_collections.length > 0
        ) {
          if (
            !isProductInCollections(
              product.collections,
              badge.criteria_collections
            )
          ) {
            badgeApplies = false;
          }
        }

        // Check stock status criteria (now includes Pre-order)
        if (
          badge.criteria_stock_status &&
          badge.criteria_stock_status !== "Any stock status"
        ) {
          if (stockStatus !== badge.criteria_stock_status) {
            badgeApplies = false;
          }
        }

        // Check tag criteria
        if (badge.criteria_tags && badge.criteria_tags.length > 0) {
          if (
            !product.tags.some((productTag) =>
              badge.criteria_tags.includes(productTag)
            )
          ) {
            badgeApplies = false;
          }
        }

        // Add applicable badges
        if (badgeApplies) {
          if (badge.type === "Discount") {
            // Only show discount badge if product is actually on sale
            if (
              product.compareAtPrice &&
              product.compareAtPrice > product.price
            ) {
              const savings = (product.compareAtPrice - product.price).toFixed(
                2
              );
              applicableBadges.push({
                ...badge,
                label: `Save £${savings}`,
                style: badge.style || "sale",
                icon: badge.icon || null,
                priority: badge.priority || 0,
              });
            }
          } else if (badge.label) {
            applicableBadges.push({
              ...badge,
              style: badge.style || "sale",
              icon: badge.icon || null,
              priority: badge.priority || 0,
            });
          }
        }
      });

      // Select highest priority badge
      const highestPriorityBadge =
        applicableBadges.length > 0
          ? applicableBadges.reduce(
              (highest, current) =>
                (current.priority || 0) > (highest.priority || 0)
                  ? current
                  : highest,
              applicableBadges[0]
            )
          : null;

      badgesToReturn[shortIdToProcess] = highestPriorityBadge;
      cache[shortIdToProcess] = highestPriorityBadge;
    } else {
      // Product not found - cache null result
      badgesToReturn[shortIdToProcess] = null;
      cache[shortIdToProcess] = null;
    }
  });

  // Update cache with new data
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        data: cache,
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    // Ignore cache write errors
  }

  return { badges: badgesToReturn };
};

/**
 * Event Listeners
 * Run badge population on three key events:
 * 1. DOMContentLoaded: For static product cards on initial page load
 * 2. bloomreach:searchResults:updated: When Bloomreach refreshes search results
 * 3. carousel:updated: Custom event fired by carousel when slides are ready
 */
document.addEventListener("DOMContentLoaded", populateProductBadges);
document.addEventListener(
  "bloomreach:searchResults:updated",
  populateProductBadges
);
document.addEventListener("carousel:updated", populateProductBadges);
